<template>
  <div class="min-h-screen bg-[#dfe7eb]">
    <!-- Session Information Header -->
    <div class="bg-white shadow-sm border-b border-slate-200 mb-6">
      <div class="p-3">
        <div class="flex flex-wrap items-start justify-start gap-6">
          <div class="flex items-center space-x-2">
            <svg class="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
              <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
            </svg>
            <span class="text-sm font-medium text-slate-700">Depot:</span>
            <span class="text-sm font-semibold text-slate-900">{{ selectedDepot.name }}</span>
          </div>

          <div class="flex items-center space-x-2">
            <svg class="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4">
              </path>
            </svg>
            <span class="text-sm font-medium text-slate-700">Area:</span>
            <span class="text-sm font-semibold text-slate-900">{{ enteredArea || 'Not specified' }}</span>
          </div>

          <div class="flex items-center space-x-2">
            <svg class="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z">
              </path>
            </svg>
            <span class="text-sm font-medium text-slate-700">Counters:</span>
            <span class="text-sm font-semibold text-slate-900">{{ counter1 || 'First counter' }} & {{ counter2 ||
              'Second counter' }}</span>
          </div>

          <div class="flex items-center space-x-2">
            <svg class="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15">
              </path>
            </svg>
            <span class="text-sm font-medium text-slate-700">
              {{ isFormOpen ? 'Auto-refresh paused' : 'Refreshing in' }}:
            </span>
            <span class="text-sm font-semibold" :class="isFormOpen ? 'text-blue-600' : 'text-slate-900'">
              {{ isFormOpen ? 'Form open' : `${countdownSeconds}s` }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <ItemsFilters />

    <div class="p-0">
      <StockTable v-if="!error" :items="items" :isloading="isloading" />
      <p v-else-if="error" class="text-red-600">{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import StockTable from '@/components/partials/stocktake/Items-Table.vue'
import { onMounted, onBeforeUnmount } from 'vue'
import useStockTake from '@/stores/stocktakestore'
import ItemsFilters from '@/components/partials/stocktake/Items-Filters.vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'

const store = useStockTake()
const route = useRoute()
const router = useRouter()

const {
  items,
  isloading,
  error,
  selectedDepot,
  counter1,
  counter2,
  enteredArea,
  countdownSeconds,
  isFormOpen,
} = storeToRefs(store)

onMounted(() => {
  // Initialize offline service first to ensure proper state detection
  store.initializeOfflineService()


  // Restore session from URL parameter
  const depotFromUrl = route.params.depot as string

  if (!depotFromUrl) {
    // No depot in URL, redirect to login
    router.push('/stocktake-app')
    return
  }


  if (!selectedDepot.value.name) {
    // Try to restore from session storage
    const sessionData = sessionStorage.getItem('stocktake-session')


    if (sessionData) {
      try {
        const session = JSON.parse(sessionData)

        if (session.depot === depotFromUrl) {
          store.startSession(session)


        } else {
          // Depot mismatch, create minimal session with just the depot
          store.startSession({
            counter1: session.counter1 || '',
            counter2: session.counter2 || '',
            area: session.area || '',
            depot: depotFromUrl
          })
        }
      } catch {
        // Invalid session data, create minimal session
        store.startSession({
          counter1: '',
          counter2: '',
          area: '',
          depot: depotFromUrl
        })
      }
    } else {
      // No session data, create minimal session with just the depot
      store.startSession({
        counter1: '',
        counter2: '',
        area: '',
        depot: depotFromUrl
      })
    }
  }

  // Start polling only after offline service is initialized
  store.startPolling()
})

onBeforeUnmount(() => {
  store.stopPolling()
})


</script>
